import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ZohoOAuthService } from '../services/ZohoOAuthService';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader } from 'lucide-react';

interface ZohoUserProfile {
  First_Name: string;
  Last_Name: string;
  Email: string;
  Display_Name: string;
  Country: string;
}

export default function ZohoCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing Zoho authentication...');
  const [userProfile, setUserProfile] = useState<ZohoUserProfile | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        if (error) {
          throw new Error(`OAuth error: ${error}`);
        }

        if (!code || !state) {
          throw new Error('Missing required OAuth parameters');
        }

        setMessage('Exchanging authorization code for access token...');
        
        // Exchange code for token
        await ZohoOAuthService.handleCallback(code, state);
        
        setMessage('Retrieving user profile...');
        
        // Get user profile
        const profile = await ZohoOAuthService.getUserProfile();
        setUserProfile(profile);
        
        setMessage('Authenticating with grandPittu...');
        
        // Login to our app with Zoho user data
        const success = await login({
          email: profile.Email,
          password: 'zoho-oauth-user'
        });
        
        if (success) {
          setStatus('success');
          setMessage(`Welcome ${profile.Display_Name}! Redirecting to dashboard...`);
          
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        } else {
          throw new Error('Failed to authenticate with grandPittu system');
        }
        
      } catch (err) {
        console.error('Zoho OAuth callback error:', err);
        setStatus('error');
        setMessage(err instanceof Error ? err.message : 'Authentication failed');
      }
    };

    handleCallback();
  }, [searchParams, navigate, login]);

  const handleRetry = () => {
    navigate('/login');
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader className="h-12 w-12 text-blue-500 animate-spin" />;
      case 'success':
        return <CheckCircle className="h-12 w-12 text-green-500" />;
      case 'error':
        return <XCircle className="h-12 w-12 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'loading':
        return 'text-blue-600';
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-background to-muted p-4">
      <div className="w-full max-w-md">
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              {getStatusIcon()}
            </div>
            <CardTitle className={`text-xl ${getStatusColor()}`}>
              {status === 'loading' && 'Authenticating...'}
              {status === 'success' && 'Authentication Successful!'}
              {status === 'error' && 'Authentication Failed'}
            </CardTitle>
            <CardDescription className="text-center">
              {message}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {userProfile && status === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="text-sm space-y-1">
                  <p><strong>Name:</strong> {userProfile.Display_Name}</p>
                  <p><strong>Email:</strong> {userProfile.Email}</p>
                  <p><strong>Country:</strong> {userProfile.Country}</p>
                </div>
              </div>
            )}
            
            {status === 'error' && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700">
                    Something went wrong during authentication. Please try again.
                  </p>
                </div>
                <Button onClick={handleRetry} className="w-full">
                  Back to Login
                </Button>
              </div>
            )}
            
            {status === 'loading' && (
              <div className="text-center">
                <div className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  <span className="text-sm text-blue-700">Please wait...</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
